/**
 * Feature catalog — Structura Food Market.
 * Define metadata de features con lifecycle states, plan tier y visibilidad en marketing.
 *
 * Arquitectura:
 * - `tenant-product-flags.ts` → qué features tiene cada tenant (runtime, DB-driven)
 * - `feature-catalog.ts`      → metadata de features para UI, marketing y docs (design-time)
 *
 * Ciclo de vida:
 *   stable   → producción, documentada, vendible
 *   beta     → disponible pero en evolución, puede cambiar
 *   upcoming → anunciada, no disponible aún
 *   deprecated → deshabilitada, en proceso de retiro
 */

export type FeatureLifecycle = "stable" | "beta" | "upcoming" | "deprecated";

export interface SmFeatureDef {
  id: string;
  name: string;
  description: string;
  lifecycle: FeatureLifecycle;
  planTier: "starter" | "pro" | "business" | "enterprise" | "all";
  /** Visible en landing y comparativa de planes */
  marketingVisible: boolean;
  /** Ícono sugerido (Lucide) */
  icon?: string;
  docsSlug?: string;
}

export const SM_FEATURES: SmFeatureDef[] = [
  // ── Starter ─────────────────────────────────────────────────────────────────
  {
    id: "catalog_public",
    name: "Tienda pública",
    description: "Storefront online en /market/:slug para que tus clientes compren sin intermediarios",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "ShoppingBag",
  },
  {
    id: "orders_online",
    name: "Pedidos online",
    description: "Carrito, checkout y seguimiento de pedidos para clientes finales",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "ClipboardList",
  },
  {
    id: "pesaje_module",
    name: "Pesaje inteligente",
    description: "Cálculo automático de precio por gramo o kilo para productos de peso variable",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "Scale",
  },
  {
    id: "stock_management",
    name: "Control de stock",
    description: "Gestión de inventario por producto con alertas de stock bajo",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "Package",
  },
  {
    id: "multi_location",
    name: "Múltiples sucursales",
    description: "Gestioná varios puntos de venta o retiro desde un solo panel",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "MapPin",
  },
  {
    id: "widget_embed",
    name: "Widget embebible",
    description: "Integrá tu tienda en cualquier sitio web con un iframe de /widget/:slug",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "Code",
  },
  {
    id: "whatsapp_notify",
    name: "WhatsApp (Kapso BYO)",
    description: "Alertas y mensajería vía tu cuenta Kapso — requiere activación prod",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "MessageCircle",
  },
  {
    id: "pwa_install",
    name: "App instalable (PWA)",
    description: "Permití instalar la tienda/panel como app en el celular (service worker + prompt de instalación)",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Smartphone",
  },
  {
    id: "invoicing_tusfacturas",
    name: "Facturación electrónica (TusFacturasAPP)",
    description: "Emití facturas AFIP/ARCA desde pedidos con tu cuenta TusFacturasAPP (BYO)",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "FileText",
  },
  {
    id: "invoicing_afip_direct",
    name: "Facturación AFIP/ARCA directo",
    description: "Emisión nativa AFIP/ARCA (WSAA/WSFE/CAEA) con certificado CUIT — sin TusFacturasAPP",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "FileText",
  },

  // ── Pro ──────────────────────────────────────────────────────────────────────
  {
    id: "payment_mercadopago",
    name: "MercadoPago (BYO)",
    description: "Conectá tu cuenta MercadoPago — checkout, transferencia y QR",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "CreditCard",
  },
  {
    id: "exports_csv",
    name: "Exportar datos",
    description: "Descargá pedidos, clientes y reportes en CSV/Excel",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Download",
    docsSlug: "exportar-datos-csv",
  },
  {
    id: "produccion_b2b",
    name: "B2B / Mayorista",
    description: "Canales mayoristas, portal institucional y pedidos B2B (todos los nichos)",
    lifecycle: "stable",
    planTier: "business",
    marketingVisible: true,
    icon: "Factory",
  },
  {
    id: "institutional_orders",
    name: "Portal institucional (alias)",
    description: "Alias de produccion_b2b — el gate runtime usa produccion_b2b. No togglear por separado.",
    lifecycle: "deprecated",
    planTier: "business",
    marketingVisible: false,
    icon: "Building2",
  },
  {
    id: "analytics_advanced",
    name: "Métricas avanzadas",
    description:
      "Dashboard con embudo nativo, abandono de checkout, RFM simple (activos / en riesgo / hibernando), tendencias, ticket y cohortes",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "BarChart3",
  },

  // ── Commerce Evolution (Fases A–D) ───────────────────────────────────────────
  {
    id: "variants_addons",
    name: "Variantes y extras",
    description: "Productos con variantes (talle, color, tamaño) y extras/addons configurables (ej: cheddar, papas, salsa)",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "SlidersHorizontal",
  },
  {
    id: "customer_accounts",
    name: "Cuentas de clientes",
    description: "Tus clientes crean su cuenta en la tienda, guardan direcciones y ven su historial de compras",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "UserCircle",
  },
  {
    id: "loyalty_points",
    name: "Programa de puntos",
    description: "Tus clientes acumulan puntos por compra y los canjean por descuento en el checkout",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Sparkles",
  },
  {
    id: "lead_capture",
    name: "Captura de emails",
    description: "Popup al primer ingreso para capturar email, nombre y fecha de nacimiento. Los datos se guardan para que el comercio los descargue (sin envíos de marketing todavía).",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Mail",
  },
  {
    id: "product_reviews",
    name: "Reseñas de productos",
    description: "Clientes dejan valoraciones y comentarios en fichas de producto del storefront",
    lifecycle: "upcoming",
    planTier: "pro",
    marketingVisible: true,
    icon: "Star",
  },
  {
    id: "referral_rewards",
    name: "Referidos con recompensa",
    description: "Invitá amigos con código de referido y recompensá a ambos por la primera compra",
    lifecycle: "upcoming",
    planTier: "pro",
    marketingVisible: true,
    icon: "Share2",
  },
  {
    id: "social_proof",
    name: "Prueba social",
    description: "Mostrá compras recientes, stock bajo y señales de confianza en el storefront",
    lifecycle: "upcoming",
    planTier: "pro",
    marketingVisible: true,
    icon: "Users",
  },
  {
    id: "loyalty_tiers",
    name: "Niveles de fidelización",
    description: "Escala bronce/plata/oro sobre el programa de puntos con beneficios por nivel",
    lifecycle: "upcoming",
    planTier: "business",
    marketingVisible: true,
    icon: "Crown",
  },
  {
    id: "merchant_quotes",
    name: "Cotizaciones",
    description: "Cotizaciones personalizadas por producto (indumentaria / moda). No es un motor de presupuestos genérico.",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: false,
    icon: "FileText",
  },
  {
    id: "gift_cards",
    name: "Gift cards",
    description: "Emitís tarjetas de regalo con código único; los clientes las usan como descuento en el checkout",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Gift",
  },
  {
    id: "feature_presets",
    name: "Comportamiento por nicho",
    description: "Configuración de comportamiento de la tienda por tipo de negocio: quick-add, galería, filtros, delivery, etc.",
    lifecycle: "stable",
    // SCRUM-449: era "pro" — TENANT_PRODUCT_FLAG_PLAN_TIER.feature_presets es "starter" (fuente de verdad runtime).
    planTier: "starter",
    marketingVisible: true,
    icon: "LayoutTemplate",
  },

  // ── Business ─────────────────────────────────────────────────────────────────
  {
    id: "custom_branding",
    name: "Branding personalizado",
    description: "Colores, logo y tipografía propios en toda la tienda y portal B2B",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Palette",
  },

  // ── Upcoming ──────────────────────────────────────────────────────────────────
  {
    id: "custom_domain",
    name: "Dominio propio",
    description: "Usá tu propio dominio (ej: tienda.tuempresa.com) en lugar del subdominio Structura",
    lifecycle: "upcoming",
    planTier: "pro",
    marketingVisible: false,
    icon: "Globe",
  },
  {
    id: "billing_lemon",
    name: "Suscripción self-serve",
    description: "Gestioná tu plan y facturación directamente desde el panel",
    lifecycle: "upcoming",
    planTier: "all",
    marketingVisible: false,
    icon: "CreditCard",
  },

  // ── Productos digitales ──────────────────────────────────────────────────────
  {
    id: "digital_products",
    name: "Productos digitales",
    description: "Venta de archivos descargables y productos sin envío — entrega automática con link seguro",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "Download",
  },

  // ── Servicios y turnos ──────────────────────────────────────────────────────
  {
    id: "service_bookings",
    name: "Reserva de turnos",
    description: "Agenda de servicios con duración, buffer y disponibilidad real por profesional",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "CalendarDays",
  },
  {
    id: "booking_widget",
    name: "Widget de reservas",
    description: "Flujo público de reserva en /turno, con branding del comercio y confirmación online",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "CalendarCheck",
  },
  {
    id: "professionals",
    name: "Profesionales",
    description: "Equipo con perfil, horarios propios y asignación de qué servicio presta cada uno",
    lifecycle: "stable",
    // SCRUM-449: era "starter" — TENANT_PRODUCT_FLAG_PLAN_TIER.professionals es "pro" (fuente de verdad runtime).
    planTier: "pro",
    marketingVisible: true,
    icon: "Users",
  },
  {
    id: "service_site_builder",
    name: "Sitio de servicios",
    description: "Storefront de estudio: categorías, servicios destacados, galería y cómo funciona la reserva",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "LayoutTemplate",
  },
  {
    id: "session_packs",
    name: "Packs de sesiones",
    description: "Bonos de varias sesiones con saldo y consumo automático al confirmar cada turno",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Layers",
  },
  {
    id: "waitlist",
    name: "Lista de espera",
    description: "Cola para turnos completos que avisa al cliente cuando se libera un horario",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Hourglass",
  },
  {
    id: "intake_forms",
    name: "Fichas de cliente",
    description: "Formularios previos al turno para antecedentes, consentimientos y datos del tratamiento",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "ClipboardCheck",
  },
  {
    id: "service_reviews",
    name: "Reseñas de servicio",
    description: "Opiniones post turno asociadas al servicio y al profesional que atendió",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Star",
  },
  {
    id: "calendar_sync",
    name: "Sincronización de agenda",
    description: "Google Calendar, Zoom y Meet: los turnos se reflejan en el calendario del profesional",
    lifecycle: "stable",
    planTier: "business",
    marketingVisible: true,
    icon: "CalendarSync",
  },
  {
    id: "commissions",
    name: "Comisiones por profesional",
    description: "Cálculo de comisión sobre cada turno completado y liquidación por profesional",
    lifecycle: "stable",
    // SCRUM-449: era "business" — TENANT_PRODUCT_FLAG_PLAN_TIER.commissions es "pro" (fuente de verdad runtime).
    planTier: "pro",
    marketingVisible: true,
    icon: "Percent",
  },

  // ── Pagos online (Payway) ───────────────────────────────────────────────────
  {
    id: "payment_online",
    name: "Pagos online",
    description: "Cobrá pedidos con tarjeta directamente en la tienda",
    lifecycle: "beta",
    // SCRUM-449: era "pro" — TENANT_PRODUCT_FLAG_PLAN_TIER.payment_online es "starter" (fuente de verdad runtime).
    planTier: "starter",
    marketingVisible: true,
    icon: "CreditCard",
  },
  {
    id: "payment_payway",
    name: "Payway (tarjetas)",
    description: "Gateway Payway/Decidir: débito y crédito con tokenización segura",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "CreditCard",
  },
  {
    id: "payment_partial",
    name: "Cobro parcial / seña",
    description: "Cobrá una seña o monto parcial del pedido",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Coins",
  },
  {
    id: "payment_marketplace_ready",
    name: "Split de pagos (marketplace)",
    description: "Distribución de pagos a múltiples comercios (próximamente)",
    lifecycle: "upcoming",
    planTier: "business",
    marketingVisible: false,
    icon: "Split",
  },

  // ── Proveedores de pago (providers-as-features) ─────────────────────────────
  { id: "payment_modo", name: "MODO", description: "Pagos con MODO — QR y deeplink", lifecycle: "beta", planTier: "pro", marketingVisible: true, icon: "Smartphone" },

  // ── Logística BYO ───────────────────────────────────────────────────────────
  {
    id: "shipping_shipnow",
    name: "Envíos Shipnow",
    description: "Conectá tu cuenta Shipnow para cotizar y despachar pedidos",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Truck",
    docsSlug: "gov-shipnow",
  },
  {
    id: "shipping_tracking",
    name: "Tracking y etiquetas",
    description: "Seguimiento de envíos y descarga de etiquetas PDF",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "PackageCheck",
    docsSlug: "gov-tracking",
  },
  {
    id: "treggo_shipping",
    name: "Envíos con Treggo",
    description: "Conectá tu cuenta Treggo para crear y rastrear envíos desde los pedidos",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Truck",
    docsSlug: "gov-treggo2",
  },
  {
    id: "andreani_shipping",
    name: "Envíos con Andreani",
    description: "Conectá tu cuenta Andreani para cotizar, crear y rastrear envíos desde los pedidos",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Package",
    docsSlug: "gov-andreani2",
  },
  {
    id: "enviopack_shipping",
    name: "Envíos con Enviopack",
    description: "Multi-carrier nacional vía Enviopack",
    // SCRUM-493 (2026-09-15): reescrito y verificado contra la API real con
    // una cuenta real — auth, cotización (GET /cotizar/costo) y creación
    // pedido+envío (POST /pedidos + POST /envios) confirmadas con respuestas
    // reales de Enviopack. Falta verificar en vivo: descarga de etiqueta con
    // un envío confirmado/procesado, y recepción real de un webhook GET
    // (?tipo=&id=) — por eso queda en beta y no stable todavía.
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Package",
    // SCRUM-245: sin artículo de ayuda propio todavía — no inventar docsSlug.
  },
  {
    id: "cabify_shipping",
    name: "Envíos Cabify (inmediato / same day)",
    description: "Cabify Logistics para entrega inmediata, same day y next day",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Bike",
    docsSlug: "gov-cabify2",
  },
  {
    id: "uber_direct_shipping",
    name: "Uber Direct",
    description: "Courier on-demand de Uber: cotizá y despachá pickups desde tu local.",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    // SCRUM-245: sin artículo de ayuda propio todavía — no inventar docsSlug.
  },
  {
    id: "delivery_provider_pedidosya",
    name: "PedidosYa Delivery",
    description: "Courier on-demand Partner POS (mock / demo Fase 1; homologación externa pendiente)",
    lifecycle: "upcoming",
    planTier: "pro",
    marketingVisible: true,
    // SCRUM-245: sin artículo de ayuda propio todavía — no inventar docsSlug.
  },
  {
    id: "delivery_provider_rapiboy",
    name: "Rapiboy",
    description: "Última milla same-day / on-demand (mock / demo Fase 1; homologación externa pendiente)",
    lifecycle: "upcoming",
    planTier: "pro",
    marketingVisible: true,
    // SCRUM-245: sin artículo de ayuda propio todavía — no inventar docsSlug.
  },
  {
    id: "oca_epak_shipping",
    name: "OCA ePak",
    description: "Cotizá, creá y etiquetá envíos OCA vía ePak (QA + prod ASMX)",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    // SCRUM-245: sin artículo de ayuda propio todavía — no inventar docsSlug.
  },
  {
    id: "correo_argentino_shipping",
    name: "Correo Argentino",
    description: "Cotizá e importá envíos con API MiCorreo (JWT + rates/import/tracking)",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    // SCRUM-245: sin artículo de ayuda propio todavía — no inventar docsSlug.
  },

  {
    id: "delivery_provider_shipnow",
    name: "ShipNow Delivery",
    description: "ShipNow expuesto como delivery provider del Logistics Hub",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: false,
  },
  {
    id: "delivery_tracking",
    name: "Delivery tracking",
    description: "Tracking unificado de deliveries",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: false,
  },
  {
    id: "delivery_quotes",
    name: "Delivery quotes",
    description: "Cotización de deliveries",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: false,
  },
  {
    id: "delivery_webhooks",
    name: "Delivery webhooks",
    description: "Ingesta y replay de webhooks de delivery",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: false,
  },
  {
    id: "delivery_dispatch",
    name: "Delivery dispatch",
    description: "Despacho / asignación de courier",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: false,
  },
  {
    id: "delivery_pickup",
    name: "Delivery pickup",
    description: "Retiro por courier en el local",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: false,
  },
  {
    id: "delivery_eta",
    name: "Delivery ETA",
    description: "ETA de deliveries",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: false,
  },

  {
    id: "zipnova_shipping",
    name: "Envíos Zipnova",
    description: "Cotizá y despachá con Zipnova (multi-carrier)",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Truck",
    docsSlug: "gov-zipnova2",
  },
  {
    id: "pickit_shipping",
    name: "Envíos Pickit",
    description: "Cotizá y despachá con Pickit (puntos + domicilio)",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Truck",
    // SCRUM-245: sin artículo de ayuda propio todavía — no inventar docsSlug.
  },
  {
    id: "shipping_multi_carrier",
    name: "Multi-carrier logística",
    description: "Cotizar y comparar múltiples carriers en checkout (ranking por precio/ETA)",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: true,
    icon: "Network",
  },
  {
    id: "payment_getnet",
    name: "Getnet",
    description: "Gateway Getnet (Santander) — checkout web",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "CreditCard",
  },
  {
    id: "payment_uala",
    name: "Ualá Bis",
    description: "Checkout con link de pago Ualá Bis (tarjeta débito/crédito)",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "CreditCard",
  },
  {
    id: "payment_nave",
    name: "Nave Negocios",
    description: "Checkout QR y tarjetas via red Galicia/Naranja X (Ranty)",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "CreditCard",
  },
  {
    id: "payment_talo",
    name: "Talo",
    description: "Cobros en BTC, USDT, Pix y más via Talo",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Bitcoin",
  },
  {
    id: "payment_sipago",
    name: "Sipago",
    description: "Checkout con tarjetas via Sipago (Geopagos)",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "CreditCard",
  },
  {
    id: "payment_dlocal",
    name: "dLocal Go",
    description: "Checkout multi-país LatAm via dLocal Go",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "CreditCard",
  },
  {
    id: "payment_qr",
    name: "Pago con QR",
    description: "Cobrá escaneando un QR generado por el comercio",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "QrCode",
  },
  {
    id: "payment_links",
    name: "Links de pago",
    description: "Generá links de cobro para enviar a tus clientes",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "Link",
  },
  {
    id: "payment_recurring",
    name: "Cobros recurrentes",
    description: "Débito automático mensual con tarjeta tokenizada — próximamente",
    lifecycle: "upcoming",
    planTier: "pro",
    marketingVisible: false,
    icon: "Repeat",
  },
  {
    id: "payment_refunds",
    name: "Reembolsos",
    description: "Reembolsos totales y parciales desde el panel",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "Undo2",
  },
  {
    id: "payment_pos_mode",
    name: "Modo POS / mostrador",
    description: "Cobro presencial en mostrador con sesión de caja, pagos mixtos y ticket fiscal",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: true,
    icon: "Store",
  },
  {
    id: "payment_pos_offline",
    name: "POS offline (continuidad)",
    description:
      "Seguí cobrando en efectivo si se cae Internet: catálogo local, cola de ventas y sincronización al reconectar",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: true,
    icon: "CloudOff",
  },
  {
    id: "inventory_bom",
    name: "Escandallos / BOM",
    description: "Gestión de ingredientes y recetas — disponibilidad derivada del stock de insumos",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: true,
    icon: "FlaskConical",
  },
  {
    id: "dine_in_tables",
    name: "Mesas y salón QR",
    description: "Gestión de mesas con estado en tiempo real, comanda por QR y cobro directo desde la mesa",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: true,
    icon: "UtensilsCrossed",
  },
  {
    id: "table_reservations",
    name: "Reservas de mesa",
    description: "Turnos configurables por el local, disponibilidad online y gestión de reservas (covers) sobre mesas y sectores",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: true,
    icon: "CalendarCheck",
  },
  {
    id: "supply_chain",
    name: "Supply Chain",
    description: "Proveedores, órdenes de compra, recepción de mercadería y trazabilidad de lotes FEFO",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: true,
    icon: "Boxes",
  },

  // ── Descuentos y promociones ──────────────────────────────────────────────
  {
    id: "discount_coupons",
    name: "Cupones de descuento",
    description: "Creá cupones de % o monto fijo para productos, categorías o toda la tienda — con límite de uso y fecha de vencimiento",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Ticket",
    docsSlug: "descuentos-hub",
  },
  {
    id: "promotions_engine",
    name: "Promociones automáticas",
    description: "Configurá 3x2, 2x1 y descuentos automáticos por cantidad — se aplican solos al carrito",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "BadgePercent",
    docsSlug: "descuentos-hub",
  },
  {
    id: "discount_wheel",
    name: "Ruleta de descuentos",
    description: "Popup gamificado que otorga un cupón al girar — captura leads y activa conversión",
    lifecycle: "upcoming",
    planTier: "pro",
    marketingVisible: true,
    icon: "Disc3",
  },
  {
    id: "abandoned_cart_incentives",
    name: "Incentivos carrito abandonado",
    description: "Cupón o beneficio automático para recuperar carritos abandonados",
    lifecycle: "upcoming",
    planTier: "pro",
    marketingVisible: true,
    icon: "ShoppingCart",
  },
  {
    id: "growth_campaigns",
    name: "Campañas de growth",
    description: "Campañas temporales de growth: embudos, promos y experimentos de conversión",
    lifecycle: "upcoming",
    planTier: "pro",
    marketingVisible: true,
    icon: "Rocket",
  },

  // ── Integraciones de marketing y analytics ────────────────────────────────
  {
    id: "integrations_gtm",
    name: "Google Tag Manager",
    description: "Conectá GTM a tu tienda para gestionar todos tus scripts de marketing desde un solo lugar sin tocar código",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Tag",
    docsSlug: "gtm-connect",
  },
  {
    id: "integrations_tiktok_pixel",
    name: "TikTok Pixel",
    description: "Medí el rendimiento de tus campañas de TikTok Ads y optimizá conversiones desde tu tienda",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "TrendingUp",
    docsSlug: "tiktok-pixel-connect",
  },
  {
    id: "integrations_hotjar",
    name: "Hotjar",
    description: "Grabaciones de sesiones, heatmaps y encuestas para entender cómo navegan tus clientes",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "MousePointer2",
    docsSlug: "hotjar-connect",
  },
  {
    id: "integrations_google_ads",
    name: "Google Ads Conversion Tracking",
    description: "Medí conversiones de tus campañas de Google Ads directamente desde el checkout de tu tienda",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "BarChart2",
    docsSlug: "google-ads-conversion-connect",
  },
  {
    id: "integrations_pinterest_tag",
    name: "Pinterest Tag",
    description: "Medí conversiones de tus campañas de Pinterest Ads y habilitá el catálogo de productos para Shopping en Pinterest",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Pin",
    docsSlug: "pinterest-tag-connect",
  },
  {
    id: "integrations_merchant",
    name: "Merchant — Personalización",
    description: "Configurador visual de estampados y bordados con flujo de cotización y aprobación. Solo actúa en checkout si el producto tiene `is_personalizable = true`. Acceso: card en hub Operaciones (cotizaciones) y hub Catalogación (config)",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Shirt",
  },
  {
    id: "integrations_google_shopping",
    name: "Google Shopping (Merchant Center)",
    description: "Feed de productos automático compatible con Google Merchant Center para aparecer en Google Shopping",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: true,
    icon: "ShoppingBag",
  },
  // ── Eventos con cupos ──────────────────────────────────────────────────────
  {
    id: "events_tickets",
    name: "Eventos con cupos",
    description: "Eventos con cupos, tickets y storefront — /app/eventos + /market/:slug/eventos",
    lifecycle: "beta",
    planTier: "starter",
    marketingVisible: true,
    icon: "Ticket",
  },
  {
    id: "events_qr_validator",
    name: "Validador QR de acceso",
    description: "Escáner QR en puerta — /app/eventos/validar",
    lifecycle: "beta",
    planTier: "starter",
    marketingVisible: true,
    icon: "QrCode",
  },
  {
    id: "push_notifications",
    name: "Push notifications",
    description: "Notificaciones push a clientes y staff vía OneSignal",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Bell",
  },
  {
    id: "email_automations",
    name: "Automatizaciones de email",
    description: "Carrito abandonado (open/click), reengagement, reseña, recompra top SKU y nudges",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Mail",
  },
  {
    id: "combos",
    name: "Combos",
    description: "Armá combos de productos con precio propio",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Layers",
  },
  {
    id: "thermal_printing",
    name: "Impresión térmica",
    description: "Tickets e impresión con QZ Tray",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Printer",
  },
  {
    id: "api_keys",
    name: "API keys",
    description: "Acceso headless a la API del tenant",
    lifecycle: "stable",
    planTier: "business",
    marketingVisible: true,
    icon: "Key",
    docsSlug: "api-keys-headless",
  },
  {
    id: "integrations_meta",
    name: "Meta Pixel",
    description: "Pixel de Meta/Facebook para remarketing",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Target",
    docsSlug: "meta-pixel-connect",
  },
  {
    id: "integrations_ga4",
    name: "Google Analytics 4",
    description: "Medición de tráfico y conversiones con GA4",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "BarChart3",
    docsSlug: "ga4-connect",
  },
  {
    id: "integrations_clarity",
    name: "Microsoft Clarity",
    description: "Heatmaps y grabaciones de sesión",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Eye",
    docsSlug: "clarity-connect",
  },
  {
    id: "custom_head_scripts",
    name: "Scripts custom (head)",
    description: "HTML/JS custom inyectado en el storefront",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: true,
    icon: "Code",
  },
  {
    id: "delivery_zones",
    name: "Zonas de delivery",
    description: "Cobertura propia: zonas (CP, localidad, provincia, área) o rangos por km",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "MapPin",
    docsSlug: "zonas-delivery",
  },
  {
    id: "sample_data",
    name: "Datos de muestra",
    description: "Seed de productos y pedidos demo",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "Sparkles",
  },
  {
    id: "product_tours",
    name: "Tours in-app",
    description: "Guías y tours de onboarding del producto",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "Compass",
    docsSlug: "tours-in-app",
  },
  {
    id: "tenant_support",
    name: "Soporte in-app",
    description: "Centro de ayuda y tickets desde el panel",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "LifeBuoy",
    docsSlug: "soporte-in-app",
  },
  {
    id: "storefront_experience",
    name: "Experiencia storefront",
    description: "Editor de bloques, SEO y layout de la tienda",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "Palette",
  },
  {
    id: "operations_suppliers",
    name: "Proveedores",
    description: "ABM de proveedores y órdenes de compra básicas",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Truck",
  },
  {
    id: "payment_config",
    name: "Configuración financiera",
    description: "Cuentas bancarias, métodos de pago y dispositivos de cobro",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Landmark",
  },
  {
    id: "inventory_advanced",
    name: "Inventario avanzado",
    description: "Historial de movimientos, alertas y devoluciones de stock",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Package",
  },
  {
    id: "product_tags",
    name: "Etiquetas de producto",
    description: "Tags para organizar y filtrar el catálogo",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Tags",
  },
  {
    id: "bulk_prices",
    name: "Ajuste masivo de precios",
    description: "Actualización de precios en lote",
    lifecycle: "stable",
    planTier: "pro",
    marketingVisible: true,
    icon: "Percent",
  },
  {
    id: "price_lists",
    name: "Listas de precios múltiples",
    description: "Gestioná varias listas de precios (una por canal: online, B2B, etc.) sin editar el producto directamente",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: true,
    icon: "ListChecks",
    docsSlug: "price-lists",
  },
  {
    id: "shelf_labels",
    name: "Etiquetas de góndola y cartelería promocional",
    description: "Imprimí etiquetas de precio y cartelería de ofertas para el local, con el precio resuelto desde tu lista de precios",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Tag",
  },
  {
    id: "media",
    name: "Biblioteca de medios",
    description: "Media library — imágenes y assets del tenant",
    lifecycle: "stable",
    planTier: "starter",
    marketingVisible: true,
    icon: "Image",
  },

  // ── Hardware Integration Platform (HIP) ────────────────────────────────────
  {
    id: "hardware",
    name: "Hardware Integration Platform",
    description: "Plataforma de integración de dispositivos POS — balanzas, impresoras, terminales de pago y más",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Cpu",
  },
  {
    id: "hardware_pos",
    name: "Terminal POS dedicada",
    description: "Conectá una terminal POS dedicada (Android/Windows) al workspace",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: true,
    icon: "Store",
  },
  {
    id: "hardware_fiscal",
    name: "Controlador fiscal",
    description: "Integración con controlador fiscal (AFIP/ARCA) para documentos fiscales",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: false,
    icon: "Receipt",
  },
  {
    id: "hardware_scale",
    name: "Balanza inteligente",
    description: "Peso en vivo o etiquetadora conectada — integrado con pesaje inteligente",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: true,
    icon: "Scale",
  },
  {
    id: "hardware_barcode",
    name: "Escáner de código de barras",
    description: "Lectura de códigos de barra por USB, Bluetooth o industrial",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: false,
    icon: "Barcode",
  },
  {
    id: "hardware_qr",
    name: "Lector QR",
    description: "Lectura de códigos QR para pagos, pedidos y validación de eventos",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: false,
    icon: "QrCode",
  },
  {
    id: "hardware_rfid",
    name: "Lector RFID",
    description: "Identificación por radiofrecuencia — antirrobo e inventario",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: false,
    icon: "Radio",
  },
  {
    id: "hardware_printer",
    name: "Impresora térmica",
    description: "Impresión de tickets, comandas y recibos vía ESC/POS",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Printer",
  },
  {
    id: "hardware_labels",
    name: "Impresora de etiquetas",
    description: "Impresión de etiquetas de precio y códigos de barra propios",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: false,
    icon: "Tag",
  },
  {
    id: "hardware_cashdrawer",
    name: "Cajón monedero",
    description: "Apertura automática de cajón monedero al cobrar",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: false,
    icon: "Wallet",
  },
  {
    id: "hardware_customer_display",
    name: "Display para el cliente",
    description: "Pantalla secundaria con el detalle de la venta en curso",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: false,
    icon: "MonitorSmartphone",
  },
  {
    // SCRUM-130 — foundation. NO es lo mismo que hardware_customer_display.
    id: "digital_signage",
    name: "Digital Signage (pantallas)",
    description:
      "Menu boards, ofertas del día y colas de turnos en TVs/pantallas del local",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: false,
    icon: "Monitor",
  },
  {
    // SCRUM-240/241 — Structura Picking: PWA de preparación de pedidos por QR.
    id: "picking_app",
    name: "Structura Picking",
    description:
      "PWA móvil vinculada por QR para preparar pedidos y recibir mercadería con cámara o lector PDA",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: false,
    icon: "ScanLine",
  },
  {
    // IA conectada (MCP): gobernada como el resto — plan (Pro+), flag por tenant y cuota (sm_plans.limits.mcp_credits_month / mcp_max_users).
    id: "mcp_ai_connected",
    name: "IA conectada (MCP)",
    description:
      "Conectá tu ChatGPT, Claude o Gemini a los datos reales de tu negocio por consultas en lenguaje natural, con cuota mensual de créditos",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "PlugZap",
  },
  {
    id: "hardware_payment_terminal",
    name: "Terminal de pago",
    description: "Cobro con tarjeta o QR desde una terminal de pago conectada",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: true,
    icon: "CreditCard",
  },
  {
    id: "hardware_data_collector",
    name: "Colector de datos",
    description: "Pistola industrial para conteos de inventario y recepciones",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: false,
    icon: "ScanLine",
  },
  {
    id: "hardware_kitchen",
    name: "Kitchen Display System",
    description: "Pantalla de cocina para comandas en tiempo real, sin papel",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: false,
    icon: "ChefHat",
  },
  {
    id: "hardware_nfc",
    name: "Lector NFC",
    description: "Lectura NFC para pagos contactless y tarjetas de fidelización",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: false,
    icon: "Nfc",
  },
  {
    id: "hardware_usb",
    name: "Conexión USB",
    description: "Conectá dispositivos de hardware por cable USB",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: false,
    icon: "Usb",
  },
  {
    id: "hardware_bluetooth",
    name: "Conexión Bluetooth",
    description: "Conectá dispositivos de hardware de forma inalámbrica",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: false,
    icon: "Bluetooth",
  },
  {
    id: "hardware_serial",
    name: "Conexión por puerto serie",
    description: "Conectá dispositivos de hardware por puerto serie / COM",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: false,
    icon: "Cable",
  },
  {
    id: "hardware_network",
    name: "Conexión por red",
    description: "Conectá dispositivos de hardware por red local (IP)",
    lifecycle: "beta",
    planTier: "business",
    marketingVisible: false,
    icon: "Network",
  },
  {
    id: "hardware_custom_drivers",
    name: "Drivers personalizados",
    description: "Desarrollo de drivers a medida para hardware no soportado de fábrica",
    lifecycle: "beta",
    planTier: "enterprise",
    marketingVisible: true,
    icon: "Code2",
  },
  {
    id: "hardware_multi_device",
    name: "Multi-dispositivo por sucursal",
    description: "Gestión de múltiples dispositivos del mismo tipo por sucursal",
    lifecycle: "beta",
    planTier: "enterprise",
    marketingVisible: true,
    icon: "Layers",
  },
  {
    id: "hardware_monitoring",
    name: "Monitoreo de hardware",
    description: "Estado, diagnóstico y alertas proactivas de todos tus dispositivos",
    lifecycle: "beta",
    planTier: "enterprise",
    marketingVisible: true,
    icon: "Activity",
  },
  {
    id: "composable_flows",
    name: "Combos (packs armables)",
    description: "Armá packs paso a paso: el cliente elige por paso y se suman precios del catálogo",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Layers",
  },
  {
    id: "ai_assistant",
    name: "Renata — Asistente IA",
    description: "Asistente operativo con IA: consultas conversacionales, Skills reales y aprobación de acciones",
    lifecycle: "beta",
    planTier: "pro",
    marketingVisible: true,
    icon: "Sparkles",
  },
  {
    // SCRUM-33 — switch de plataforma para habilitar Legal+KYC por tenant (testing).
    id: "legal_kyc_gate",
    name: "Legal + KYC (Didit)",
    description:
      "Aceptación de documentos legales y verificación de identidad del Owner vía Didit. Apagado por default en todos los planes.",
    lifecycle: "beta",
    planTier: "starter",
    marketingVisible: false,
    icon: "ShieldCheck",
  },
];

/** Features visibles en marketing por plan tier */
export function getMarketingFeatures(planTier?: SmFeatureDef["planTier"]): SmFeatureDef[] {
  return SM_FEATURES.filter(
    (f) =>
      f.marketingVisible &&
      f.lifecycle !== "deprecated" &&
      (!planTier || f.planTier === planTier || f.planTier === "all"),
  );
}

/** Feature por id */
export function getFeatureById(id: string): SmFeatureDef | undefined {
  return SM_FEATURES.find((f) => f.id === id);
}

/** Features en beta — para badge en UI */
export const BETA_FEATURE_IDS = SM_FEATURES.filter((f) => f.lifecycle === "beta").map((f) => f.id);

/** Features próximas — para banner de "coming soon" */
export const UPCOMING_FEATURE_IDS = SM_FEATURES.filter((f) => f.lifecycle === "upcoming").map(
  (f) => f.id,
);

/**
 * Features deshabilitadas/retiradas — nunca deben ofrecerse a un tenant real
 * ni demo (ver la propia `description` de cada una: alias sin gate propio,
 * uso interno de plataforma, etc.). SCRUM-254: usado para que el force-true
 * de demos no las prenda igual que el resto del catálogo.
 */
export const DEPRECATED_FEATURE_IDS = SM_FEATURES.filter((f) => f.lifecycle === "deprecated").map(
  (f) => f.id,
);
